import type { LineSeries } from '@nyce/admin-kit/console';
import type { DailyRow, MetricId, MetricTotals } from './api/prodstats';

export const DAY = 86_400_000;

/** Midnight UTC of a `yyyy-MM-dd` day (the chart's time axis). */
export const dayMs = (day: string) => Date.parse(`${day}T00:00:00Z`);

export const addDays = (day: string, n: number) => new Date(dayMs(day) + n * DAY).toISOString().slice(0, 10);

/** The days of [from, to). */
export function daysOf(from: string, to: string): string[] {
  const out: string[] = [];
  for (let t = dayMs(from); t < dayMs(to); t += DAY) out.push(new Date(t).toISOString().slice(0, 10));
  return out;
}

/** What the chart shows of the transaction types: the largest ones, the rest as one line. */
export const TOP_TYPES = 5;
export const OTHER = 'Other types';

/**
 * One metric's rows as day series on [from, to): one line, or for transactions the `TOP_TYPES` largest types (over the
 * whole range) plus "Other types". A day without rows is 0, not a gap — nothing was added.
 */
export function dailySeries(
  rows: DailyRow[],
  metric: MetricId,
  label: string,
  byType: boolean,
  from: string,
  to: string
): LineSeries[] {
  const days = daysOf(from, to);
  const mine = rows.filter((r) => r.metric === metric);
  const line = (key: string, name: string, pick: (r: DailyRow) => boolean): LineSeries => {
    const perDay = new Map<string, number>();
    for (const r of mine) if (pick(r)) perDay.set(r.day, (perDay.get(r.day) ?? 0) + r.count);
    return { key, label: name, points: days.map((d) => ({ t: dayMs(d), v: perDay.get(d) ?? 0 })) };
  };
  if (!byType) return [line(metric, label, () => true)];
  const totals = new Map<string, number>();
  for (const r of mine) totals.set(r.typeCode, (totals.get(r.typeCode) ?? 0) + r.count);
  const ranked = [...totals.entries()]
    .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
    .map(([t]) => t);
  const top = ranked.slice(0, ranked.length > TOP_TYPES + 1 ? TOP_TYPES : TOP_TYPES + 1);
  const series = top.map((t) => line(t, t || 'No type', (r) => r.typeCode === t));
  if (ranked.length > top.length) series.push(line('other', OTHER, (r) => !top.includes(r.typeCode)));
  return series;
}

/** Each type's total over the rows of one metric, largest first. */
export function typeTotals(rows: DailyRow[], metric: MetricId): { typeCode: string; count: number }[] {
  const totals = new Map<string, number>();
  for (const r of rows)
    if (r.metric === metric) totals.set(r.typeCode, (totals.get(r.typeCode) ?? 0) + r.count);
  return [...totals.entries()]
    .map(([typeCode, count]) => ({ typeCode, count }))
    .sort((a, b) => b.count - a.count);
}

/** Customer + purchase deliveries, and their lines, of one window of the totals. */
export function headline(metrics: MetricTotals[], pick: (m: MetricTotals) => number) {
  const v = (id: MetricId) => {
    const m = metrics.find((x) => x.id === id);
    return m ? pick(m) : 0;
  };
  return {
    transactions: v('transactions'),
    deliveries: v('cod') + v('pd'),
    lines: v('cod_lines') + v('pd_lines'),
    shipments: v('shipment_refs'),
  };
}
